/**
 * Lazy PostHog loader.
 *
 * Guarantees:
 *  - posthog-js is only dynamically imported after all guards pass
 *    (browser + consent === 'granted' + config present + not on /admin/*).
 *  - posthog.init() runs at most once per page load (idempotent).
 *  - Session recording is disabled.
 *  - No identify() calls; distinct IDs remain anonymous.
 *
 * Consent transitions are driven by callers via revokeAndReset() and
 * reinstate(); this module never subscribes to consent itself.
 */

import { analyticsConfig } from "@/lib/analytics-config";
import { getConsent } from "./consent";
import { isAdminPath } from "./route-utils";

type PostHogModule = typeof import("posthog-js")["default"];

let singleton: PostHogModule | null = null;
let initPromise: Promise<PostHogModule | null> | null = null;
let initCallCount = 0;
let active = false; // true while we should be capturing

function currentPath(): string {
  if (typeof window === "undefined") return "";
  return window.location?.pathname ?? "";
}

function configPresent(): boolean {
  return Boolean(analyticsConfig.posthog?.key && analyticsConfig.posthog?.host);
}

export function isConfigPresent(): boolean {
  return configPresent();
}

export function isReady(): boolean {
  return singleton !== null && active;
}

/** Test/verification hook — how many times posthog.init() has actually been invoked. */
export function _getInitCallCount(): number {
  return initCallCount;
}

/**
 * Ensure PostHog is loaded and initialized. All guards checked here.
 * Idempotent: repeated calls resolve to the same singleton without re-init.
 */
export async function ensureInitialized(): Promise<PostHogModule | null> {
  if (typeof window === "undefined") return null;
  if (!configPresent()) return null;
  if (getConsent() !== "granted") return null;
  if (isAdminPath(currentPath())) return null;

  if (singleton) {
    active = true;
    return singleton;
  }
  if (initPromise) return initPromise;

  initPromise = (async () => {
    try {
      const mod = await import("posthog-js");
      const posthog = mod.default;
      // Re-check guards after the dynamic import resolves — consent may have
      // flipped or route may have changed while the chunk was loading.
      if (getConsent() !== "granted" || isAdminPath(currentPath())) {
        initPromise = null;
        return null;
      }
      posthog.init(analyticsConfig.posthog!.key, {
        api_host: analyticsConfig.posthog!.host,
        autocapture: false,
        capture_pageview: false,
        capture_pageleave: true,
        disable_session_recording: true,
        mask_all_text: false,
        respect_dnt: true,
        persistence: "localStorage+cookie",
        // Project has `defaultIdentifiedOnly: true`; without this, anonymous
        // pageviews are dropped client-side and never reach `/i/v0/e/`.
        person_profiles: "always",
      });
      initCallCount += 1;
      singleton = posthog;
      active = true;
      return posthog;
    } catch (err) {
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.debug("[analytics] posthog init failed", err);
      }
      initPromise = null;
      return null;
    }
  })();

  return initPromise;
}

/** Fire an event when initialized. Silently no-ops otherwise. */
export async function capture(
  event: string,
  props: Record<string, string | number | boolean>,
): Promise<void> {
  const ph = await ensureInitialized();
  if (!ph || !active) return;
  try {
    ph.capture(event, props);
  } catch {
    /* noop */
  }
}

/** Emit a pageview. Caller is responsible for deduping. */
export async function capturePageview(path: string, title?: string): Promise<void> {
  const ph = await ensureInitialized();
  if (!ph || !active) return;
  try {
    ph.capture("$pageview", { $current_url: path, page_title: title ?? "" });
  } catch {
    /* noop */
  }
}

/** Consent revoked: opt out, clear identifiers, and stop future captures. */
export function revokeAndReset(): void {
  active = false;
  if (!singleton) return;
  try {
    singleton.opt_out_capturing();
    singleton.reset(true);
  } catch {
    /* noop */
  }
}

/**
 * Consent granted (again). Idempotent: if the singleton already exists we
 * only opt-in; we do NOT call posthog.init() a second time.
 */
export async function reinstate(): Promise<void> {
  if (singleton) {
    active = true;
    try {
      singleton.opt_in_capturing();
    } catch {
      /* noop */
    }
    return;
  }
  await ensureInitialized();
}